import {connect} from "redux-vue";
import * as locationActions from "../actions/location";
import * as stakeholderActions from "../actions/stakeholder";
import * as notificationsActions from "../actions/notifications";
import {testStakeholderIsStaff} from "../helpers/tools";
import {ADOPTION_TARGET} from "../helpers/consts";
import classNames from "classnames";

const LocationItem = {
  props: ['actions', 'location', 'setLocation', 'setStakeholder', 'notifications', 'dismissNotification', 'muteNotification'],
  computed: {
    alertGoalReached() {
      const notification = this.notifications.collection
        .find(each => each.type === 'LocationReachedGoal' && each.id === this.location.id);
      return notification && !notification.mute;
    }
  },
  methods: {
    /**
     *
     * @param location
     * @param stakeholder
     * @returns {Function}
     */
    showMoreInformation(location, stakeholder = null) {
      return () => {
        this.setLocation(location);
        this.setStakeholder(stakeholder);
        const notification = this.findNotification(stakeholder || location);
        notification && this.dismissNotification(notification);
      }
    },
    findNotification(content) {
      return this.notifications.collection
        .find(each => (['Location', 'Stakeholder'].includes(each.type) && each.id === content.id));
    },
    isNew(content) {
      return content && !!this.findNotification(content);
    },
    handleDismissAlertGoalReached() {
      const notification = this.notifications.collection
        .find(each => each.type === 'LocationReachedGoal' && each.id === this.location.id);
      if (notification) {
        this.muteNotification(notification);
      }
      this.$nextTick(() => {
        tippy('[data-tippy-content]', {
          placement: 'bottom'
        });
      })
    },
    makeStaffClass(staff, index) {
      return classNames(
        "transform scale-90 lg:scale-100 flex items-center mb-2 lg:mb-0",
        index === 0 && this.actions.history.length > 0 ? 'ml-auto' : 'lg:ml-6'
      );
    },
    makeDiffLabel(num, prevNum) {
      const dif = num - prevNum;
      const opr = dif < 0 ? '' : '+';
      return opr+dif;
    }
  },
  render(h) {
    const location = this.location;
    const staff = location.stakeholders.filter(testStakeholderIsStaff);
    const community = location.stakeholders.find(each => !testStakeholderIsStaff(each));
    const alertGoalReached = this.alertGoalReached && (
      <div class="absolute top-0 left-0 bottom-0 right-0 rounded z-30 overflow-hidden m-1">
        <div
          class="p-3 text-center flex flex-col items-center justify-center relative z-30 text-white h-full w-full">
          <p class="text-xl lg:text-2xl font-bold leading-snug">
            Congratulations!
          </p>
          <img src="/img/changemanagement/img-trophy-alt.svg" alt="" class="w-20"/>
          <p class="text-xs leading-snug lg:w-10/12 mx-auto my-3">
            You have reached your goal of reaching at least 80% adoption. The lessons learned during this implementation
            will be applied to national level recommendations for rollout to the entire country.
          </p>
          <button type="button" onClick={this.handleDismissAlertGoalReached}
                  class="mx-auto rounded-full bg-brand-orange-700 text-white uppercase py-1 px-6 text-sm hover:bg-orange-600 focus:outline-none transition-colors duration-100">
            Continue
          </button>
        </div>
        <div class="absolute top-0 left-0 bottom-0 right-0" style="background: #707070EF"></div>
      </div>
    );
    const adoptionMeter = (
      <div class="relative pb-8 lg:-mt-4 z-10">
        <small>Adoption</small>
        {location.name == "District Health Team" &&
        <span data-tippy-content="Implementation of the change at this location">
             <i class="mdi mdi-help-circle text-brand-orange-700 text-sm lg:text-base"></i>
            </span>}
        <div class="bg-brand-blue-400 rounded-full w-full overflow-hidden relative z-10">
          <div class="bg-brand-green-300 rounded-full p-1"
               style={`width: ${location.adoption}%; box-shadow: 6px 0px 2px #00000029;`}>
          </div>
        </div>
        <div class="absolute overflow-visible text-xs w-0 transform -translate-y-4"
             style={`left: ${location.adoption - 3 < 0 ? 0 : location.adoption - 3}%;`}>
          <div class="h-6 w-px bg-gray-900 mx-auto ml-2"></div>
          <div class="inline-flex items-center">
            {location.adoption}%
            {!isNaN(location.prev_adoption) && location.prev_adoption < location.adoption && (
              <span data-tippy-content={this.makeDiffLabel(location.adoption, location.prev_adoption)}>
                <i class="mdi mdi-arrow-up-thick text-brand-green-600 text-xs"></i>
              </span>
            )}
            {!isNaN(location.prev_adoption) && location.prev_adoption > location.adoption && (
              <span data-tippy-content={this.makeDiffLabel(location.adoption, location.prev_adoption)}>
                <i class="mdi mdi-arrow-down-thick text-red-500 text-xs"></i>
              </span>
            )}
          </div>
        </div>
      </div>
    );
    const communityMeter = community && (
      <div class="relative pb-10 lg:pb-6 lg:-mt-4">
        <small>Community Participation</small>
        {location.id === "FacilityA" && (
          <span class="relative z-10" data-tippy-content="Active community participation in routine immunization planning">
            <i class="mdi mdi-help-circle text-brand-orange-700 text-sm lg:text-base"></i>
          </span>
        )}
        <div class="bg-brand-blue-400 rounded-full w-full overflow-hidden relative z-10">
          <div class="bg-brand-green-300 rounded-full p-1" style={`width: ${community.participation}%; box-shadow: 6px 0px 2px #00000029;`}>
          </div>
        </div>
        <div class="absolute overflow-visible text-xs w-0 transform -translate-y-4"
             style={`left: ${community.participation - 3 < 0 ? 0 : community.participation - 3}%;`}>
          <div class="h-6 w-px bg-gray-900 mx-auto ml-2"></div>
          <div class="inline-flex items-center">
            {community.participation}%
            {!isNaN(community.prev_participation) && community.prev_participation < community.participation && (
              <span data-tippy-content={this.makeDiffLabel(community.participation, community.prev_participation)}>
                <i class="mdi mdi-arrow-up-thick text-brand-green-600 text-xs"></i>
              </span>
            )}
            {!isNaN(community.prev_participation) && community.prev_participation > community.participation && (
              <span data-tippy-content={this.makeDiffLabel(community.participation, community.prev_participation)}>
                <i class="mdi mdi-arrow-down-thick text-red-500 text-xs"></i>
              </span>
            )}
          </div>
        </div>
      </div>
    );
    return (
      <fragment>
        {/*{alertGoalReached}*/}
        <div class="w-full h-full rounded bg-brand-gray-300 p-5 pb-0">
          <div class="flex items-center justify-center">
            <p class="text-xl text-center leading-none">
              {location.name}
            </p>
            <div class="w-2 lg:w-3"></div>
            {location.adoption >= ADOPTION_TARGET && (
              <span data-tippy-content="Met your goal of reaching at least 80% adoption" class="z-20">
                <img src="/img/changemanagement/img-trophy-alt.svg" alt="" class="w-6 lg:w-8"/>
              </span>
            )}
          </div>
          {adoptionMeter}
          {communityMeter}
          <ul class="flex flex-wrap items-start justify-evenly">
            <li class="transform scale-90 lg:scale-100 flex items-center">
              <div class="flex flex-col items-center">
                <div class="relative">
                  <transition
                    name="custom-classes-transition"
                    enter-active-class="transition ease-out duration-300 transform"
                    enter-class="opacity-0 scale-0"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-200 transform"
                    leave-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-0"
                  >
                    {this.isNew(community || location) && (
                      <div class="absolute h-5 lg:h-6 lg:w-6 right-0 top-0 w-5 z-10">
                        <div data-tippy-content="New content available">
                          <img onClick={this.showMoreInformation(location, community)}
                               src="/img/changemanagement/new-badge.svg" alt="NEW" class="w-full h-full"/>
                        </div>
                      </div>
                    )}
                  </transition>
                  <img onClick={this.showMoreInformation(location, community)} src={location.img} alt=""
                       class="w-16 lg:w-20 cursor-pointer transform hover:scale-105"/>
                </div>
                <div class="text-xs text-center w-20">
                  {location.name}
                </div>
              </div>
            </li>
            {staff.map((staff, index) => (
              <li key={staff.id} class={this.makeStaffClass(staff, index)}>
                <div class="flex flex-col items-center relative">
                  <transition
                    name="custom-classes-transition"
                    enter-active-class="transition ease-out duration-300 transform"
                    enter-class="opacity-0 scale-0"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-200 transform"
                    leave-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-0"
                  >
                    {this.isNew(staff) && (
                      <div class="absolute h-5 lg:h-6 lg:w-6 right-0 top-0 w-5 z-10">
                        <div data-tippy-content="New content available">
                          <img onClick={this.showMoreInformation(location, staff)}
                               src="/img/changemanagement/new-badge.svg" alt="NEW" class="w-full h-full"/>
                        </div>
                      </div>
                    )}
                  </transition>
                  <img src={staff.img} alt="" class="w-16 lg:w-20 cursor-pointer transform hover:scale-105"
                       onClick={this.showMoreInformation(location, staff)}/>
                  <div class="text-xs text-center w-16 lg:w-auto">
                    {staff.title}
                  </div>
                </div>
                {this.actions.history.length > 0 && (
                  <ul class="pl-1 text-xs transform -translate-y-1">
                    <li class="lg:mb-1">
                      <div class="relative">
                        <div class="bg-white rounded-full p-px border-white border w-20">
                          <div class="bg-brand-orange-500 rounded-full p-1" style={`width: ${staff.motivation}%`}>
                          </div>
                        </div>
                        {location.name == "District Health Team" && staff.title == "EPI Focal Point" && (
                          <div data-tippy-content="Willingness and enthusiasm to implement the change"
                               class="leading-none absolute top-0 right-0 -mt-px lg:-mt-1 -mr-4">
                           <i class="mdi mdi-help-circle text-brand-orange-700 text-sm lg:text-base"></i>
                          </div>
                        )}
                      </div>
                      <small class="pl-1 py-px">
                        Motivation: {staff.motivation}%
                      </small>
                      {!isNaN(staff.prev_motivation) && staff.prev_motivation < staff.motivation && (
                        <span data-tippy-content={this.makeDiffLabel(staff.motivation, staff.prev_motivation)}>
                          <i class="mdi mdi-arrow-up-thick text-brand-green-600 text-xs leading-none"></i>
                        </span>
                      )}
                      {!isNaN(staff.prev_motivation) && staff.prev_motivation > staff.motivation && (
                        <span data-tippy-content={this.makeDiffLabel(staff.motivation, staff.prev_motivation)}>
                          <i class="mdi mdi-arrow-down-thick text-red-500 text-xs leading-none"></i>
                        </span>
                      )}
                    </li>
                    <li>
                      <div class="relative">
                        <div class="bg-white rounded-full p-px border-white border w-20">
                          <div class="bg-brand-teal-500 rounded-full p-1" style={`width: ${staff.ability}%`}>
                          </div>
                        </div>
                        {location.name == "District Health Team" && staff.title == "EPI Focal Point" && (
                          <div data-tippy-content="Knowledge, skills, and authority to effectively implement the change"
                               class="leading-none absolute top-0 right-0 -mt-px lg:-mt-1 -mr-4">
                            <i class="mdi mdi-help-circle text-brand-orange-700 text-sm lg:text-base"></i>
                          </div>
                        )}
                      </div>
                      <small class="pl-1 py-px">
                        Abilities: {staff.ability}%
                      </small>
                      {!isNaN(staff.prev_ability) && staff.prev_ability < staff.ability && (
                        <span data-tippy-content={this.makeDiffLabel(staff.ability, staff.prev_ability)}>
                          <i class="mdi mdi-arrow-up-thick text-brand-green-600 text-xs leading-none"></i>
                        </span>
                      )}
                      {!isNaN(staff.prev_ability) && staff.prev_ability > staff.ability && (
                        <span data-tippy-content={this.makeDiffLabel(staff.ability, staff.prev_ability)}>
                          <i class="mdi mdi-arrow-down-thick text-red-500 text-xs leading-none"></i>
                        </span>
                      )}
                    </li>
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      </fragment>
    );
  }
};

function mapStateToProps(state) {
  return {
    actions: state.simulation.actions,
    locations: state.simulation.locations,
    notifications: state.simulation.notifications,
  };
}

function mapActionToProps(dispatch) {
  return {
    setLocation: (location) => dispatch(locationActions.setLocation(location)),
    setStakeholder: (stakeholder) => dispatch(stakeholderActions.setStakeholder(stakeholder)),
    muteNotification: (notification) => dispatch(notificationsActions.muteNotification(notification)),
    dismissNotification: (notification) => dispatch(notificationsActions.dismissNotification(notification)),
  };
}

export default connect(mapStateToProps, mapActionToProps)(LocationItem);
